// Generated by tutorials/record.cjs from tutorials/articles/h47-auto-item-mods.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h47 --text-only` (wording) or
// `node tutorials/record.cjs h47` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'auto-item-mods',
  catalogId: 'h47',
  title: 'Auto Item Mods (AIM)',
  summary: 'An Auto Item Mod is a rule on an item: for this diet or texture, serve that instead. Set the rules once on the item, and every menu that serves it can fill in each diet\'s version for you.',
  audience: 'Menu planners who set up what each diet and texture gets',
  area: 'menus',
  learn: [
    'What an Auto Item Mod rule is',
    'Find the rules for an item',
    'Add a rule to an item',
    'Change or remove a rule',
  ],
  keywords: [
    'aim',
    'auto item mod',
    'auto item mods',
    'auto item modification',
    'substitution',
    'substitute',
    'swap item',
    'diet rule',
    'texture',
    'pureed',
    'mechanical soft',
    'renal',
    'do not serve',
    'fill item',
  ],
  steps: [
    {
      id: 'open',
      part: 'Find an item\'s Auto Item Mods',
      heading: 'Open Auto Item Mods',
      text: 'In the menu, select Menus, then Auto Item Mods, and pick a menu cycle under Menu Cycle. You see the rules of every item the cycle can serve.\n\nEach row is one rule: for the diet order under Diet Order, the item is changed to what Change To says, or not served at all. Change To can be another item, or the same item in another texture.',
      media: {
        image: {
          src: '/help/auto-item-mods/open.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/auto-item-mods/open.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.6,
        },
        alt: 'The Auto Item Mods page for Fall/Winter Cycle 2026, listing rules such as BACON not served on Cardiac and BANANA PUDDING changed to PUDDING VANILLA SF on Consistent Carbohydrate.',
      },
    },
    {
      id: 'find',
      part: 'Find an item\'s Auto Item Mods',
      heading: 'Find an item\'s rules',
      text: 'Type the item\'s name in Search rules. Its rules are listed in order, numbered under #.\n\nHere HAM BAKED becomes TURKEY ROAST for Cardiac and Low Sodium 2g, PORK LOIN ROAST for Renal, and is served in the resident\'s texture for Mechanical Soft, Minced and Moist and Pureed.',
      media: {
        image: {
          src: '/help/auto-item-mods/find.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/auto-item-mods/find.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.7,
        },
        alt: 'Search rules holds ham baked, and the six rules of HAM BAKED are listed: Cardiac, Renal, Low Sodium 2g, Mechanical Soft, Minced and Moist and Pureed.',
      },
    },
    {
      id: 'open-item',
      part: 'Change an item\'s rules',
      heading: 'Open the item',
      text: 'Select one of the item\'s rules, then Open Item (or double-click the rule). The item opens over the list on its Auto Item Mods section, where its rules are changed. Close it to come back to the list, which shows the changes once they are saved.',
      media: {
        image: {
          src: '/help/auto-item-mods/open-item.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/auto-item-mods/open-item.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'The HAM BAKED item page on Auto Item Mods, listing its six rules with the Result, Item and Consistency of each.',
      },
    },
    {
      id: 'add-rule',
      part: 'Change an item\'s rules',
      heading: 'Add a rule',
      text: 'Select Add Rule. Under Diet order, add the diet type, the texture, or both for a combination. Under Result, pick Change the item, then the item to serve instead.\n\nAnything you leave blank, such as Size, stays as it is.',
      media: {
        image: {
          src: '/help/auto-item-mods/add-rule.webp',
          width: 1280,
          height: 1052,
        },
        clip: {
          src: '/help/auto-item-mods/add-rule.mp4',
          width: 1280,
          height: 1052,
          durationSec: 6,
        },
        alt: 'The Add Auto Item Modification window with Finger Foods added as the diet order, Result Change the item, Item SANDWICH HAM AND CHEESE and Size 1 ea.',
      },
    },
    {
      id: 'rule-added',
      part: 'Change an item\'s rules',
      heading: 'Check where the rule sits',
      text: 'Select OK. The rule is added to the list. When two rules match a diet order equally well, the higher one wins; move a rule with the up and down arrows at the right.',
      media: {
        image: {
          src: '/help/auto-item-mods/rule-added.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/auto-item-mods/rule-added.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.2,
        },
        alt: 'HAM BAKED\'s rules with a seventh rule: Finger Foods, Change, SANDWICH HAM AND CHEESE.',
      },
    },
    {
      id: 'edit-rule',
      part: 'Change an item\'s rules',
      heading: 'Change or remove a rule',
      text: 'Select the pencil on a rule to change it, in the same window. The bin at the end of the row removes the rule.\n\nHere Renal residents get PORK LOIN ROAST instead of the ham.',
      media: {
        image: {
          src: '/help/auto-item-mods/edit-rule.webp',
          width: 1280,
          height: 1052,
        },
        clip: {
          src: '/help/auto-item-mods/edit-rule.mp4',
          width: 1280,
          height: 1052,
          durationSec: 2,
        },
        alt: 'The Edit Auto Item Modification window for the Renal rule, changing HAM BAKED to PORK LOIN ROAST.',
      },
    },
    {
      id: 'save',
      part: 'Change an item\'s rules',
      heading: 'Save the item',
      text: 'Select Save at the bottom of the page. The rules are saved with the item, and are used the next time a menu row with this item is filled in.',
      media: {
        image: {
          src: '/help/auto-item-mods/save.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/auto-item-mods/save.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.1,
        },
        alt: 'The HAM BAKED item page after saving, with its seven rules and a message that the item was saved.',
      },
    },
  ],
  tips: [
    'Set up the rules on items you serve often first: entrees, breads, desserts and drinks. Every menu that serves the item benefits.',
    'In a menu cycle, AIM - Fill Item applies an item\'s rules to a row, so each diet column gets its version in one step. See Changing items and rows.',
    'Audit, on the item\'s Auto Item Mods section, asks which saved rule a diet order would get. Use it to check a combination, such as Renal and Pureed.',
    'Pick All items under Menu Cycle to see the rules of every item, not only the ones on a cycle.',
  ],
  notes: [
    'Each rule matches a diet order: one diet type or texture, or two or more together for a combination. The best match wins; when two rules match equally, the one higher in the list wins.',
    'When no rule matches says what happens to a diet order that no rule covers: reset to the item\'s defaults, leave the item as it is, or use a custom No Match rule.',
    'Rule changes are saved with the item. Menus already filled in keep what they have until you fill the row again.',
    'Changing an item\'s rules needs the right role. If Add Rule is missing, ask your administrator.',
  ],
  related: ['changing-menu-items', 'diet-and-consistency-rules', 'reading-a-menu-cycle', 'menu-cycles'],
  recorded: '2026-10-26',
};

export default article;
